import * as React from "react"
import { cn } from "../../lib/utils"
import { motion } from "framer-motion"

const ModernCard = React.forwardRef(({ 
  className, 
  children, 
  variant = "default",
  hover = true,
  animate = true,
  ...props 
}, ref) => {
  const variants = {
    default: "bg-card",
    gradient: "bg-gradient-to-br from-purple-500 to-pink-500 text-white",
    glass: "bg-white/10 backdrop-blur-lg border-white/20",
  }

  const CardComponent = animate ? motion.div : 'div'
  
  return (
    <CardComponent
      ref={ref}
      className={cn(
        "rounded-xl border shadow-lg p-6",
        variants[variant],
        hover && "transition-all duration-300 hover:shadow-xl hover:-translate-y-1",
        className
      )}
      {...(animate && {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 }
      })}
      {...props}
    >
      {children}
    </CardComponent>
  )
})
ModernCard.displayName = "ModernCard"

const StatCard = React.forwardRef(({ 
  className,
  title,
  value,
  icon: Icon,
  trend,
  trendValue,
  color = "primary",
  ...props 
}, ref) => {
  const colors = {
    primary: "from-purple-500 to-purple-600",
    success: "from-green-500 to-green-600",
    warning: "from-yellow-500 to-yellow-600",
    danger: "from-red-500 to-red-600",
    info: "from-blue-500 to-blue-600",
  }

  return (
    <motion.div
      ref={ref}
      className={cn(
        "rounded-xl bg-gradient-to-br text-white shadow-lg p-6 relative overflow-hidden",
        colors[color],
        className
      )}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      {...props}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-90">{title}</p>
          <h3 className="text-3xl font-bold mt-2">{value}</h3>
          {trend && (
            <p className={cn(
              "text-sm mt-2 flex items-center gap-1",
              trend === "up" ? "text-green-100" : "text-red-100"
            )}>
              {trend === "up" ? "↑" : "↓"} {trendValue}
            </p>
          )}
        </div>
        {Icon && (
          <div className="bg-white/20 rounded-full p-3">
            <Icon className="w-8 h-8" />
          </div>
        )}
      </div>
      <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full" />
    </motion.div>
  )
})
StatCard.displayName = "StatCard"

export { ModernCard, StatCard }
